import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { clientPrices } from './conceptPricing';

/* PDF de Actualización de Presupuesto — plantilla oscura tipo cotización, generado 100% en el navegador */
export const IVA_RATE = 0.16;

const C = {
  bg: [6, 12, 24],          // #060C18
  row: [13, 27, 46],        // #0D1B2E
  footer: [18, 32, 64],     // #122040
  blue: [0, 102, 255],      // #0066FF
  cyan: [0, 207, 255],      // #00CFFF
  orange: [255, 107, 0],    // #FF6B00
  white: [255, 255, 255],
  text: [226, 234, 244],    // #E2EAF4
  muted: [138, 160, 190],
};

const fmtMXN = (v) => `$${(Number(v) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtQty = (q) => (Math.abs(q) < 0.01 && q !== 0 ? q.toFixed(4) : q.toFixed(2));

const toBase64 = (buffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
};

const fetchBase64 = async (url) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`No se pudo cargar ${url}`);
  return toBase64(await res.arrayBuffer());
};

// Poppins (Regular/Bold). Si no se puede cargar, se usa Helvetica.
const registerFonts = async (doc) => {
  try {
    const [regular, bold] = await Promise.all([fetchBase64('/fonts/Poppins-Regular.ttf'), fetchBase64('/fonts/Poppins-Bold.ttf')]);
    doc.addFileToVFS('Poppins-Regular.ttf', regular);
    doc.addFont('Poppins-Regular.ttf', 'Poppins', 'normal');
    doc.addFileToVFS('Poppins-Bold.ttf', bold);
    doc.addFont('Poppins-Bold.ttf', 'Poppins', 'bold');
    return 'Poppins';
  } catch (e) {
    console.warn('Poppins no disponible, usando Helvetica', e);
    return 'helvetica';
  }
};

const loadLogo = async () => {
  try {
    const res = await fetch('/icon-192.png');
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const paintBackground = (doc) => {
  doc.setFillColor(...C.bg);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), doc.internal.pageSize.getHeight(), 'F');
};

const sectionTitle = (doc, font, title, y) => {
  doc.setFont(font, 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...C.cyan);
  doc.text(title, 14, y);
  doc.setFillColor(...C.blue);
  doc.rect(14, y + 1.5, 22, 0.8, 'F');
  return y + 7;
};

const drawFooter = (doc, font, pageNumber) => {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  doc.setFillColor(...C.footer);
  doc.rect(0, h - 12, w, 12, 'F');
  doc.setFont(font, 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...C.text);
  doc.text('Generado con GenerIA · Levantamiento de presupuesto en campo', 14, h - 5);
  doc.text(`Página ${pageNumber}`, w - 14, h - 5, { align: 'right' });
};

export const buildFolio = (numeroContrato) => {
  const d = new Date();
  const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}-${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}`;
  const base = (numeroContrato || 'PRES').replace(/[^A-Za-z0-9]/g, '').slice(0, 10).toUpperCase() || 'PRES';
  return `${base}-${stamp}`;
};

/* Devuelve { blob, filename }
   priceMode: 'mo' (solo mano de obra) | 'material' (con material) | 'both' (ambas) */
export const generatePresupuestoPdf = async ({ projectName, numeroContrato, ubicacion, rows, generatedBy, priceMode = 'both' }) => {
  const showMo = priceMode === 'mo' || priceMode === 'both';
  const showMat = priceMode === 'material' || priceMode === 'both';
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const font = await registerFonts(doc);
  const logo = await loadLogo();
  const w = doc.internal.pageSize.getWidth();
  const fecha = new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
  const folio = buildFolio(numeroContrato);

  paintBackground(doc);

  // ── Encabezado ──
  if (logo) doc.addImage(logo, 'PNG', 14, 10, 18, 18);
  doc.setFont(font, 'bold');
  doc.setFontSize(20);
  doc.setTextColor(...C.white);
  doc.text('GenerIA', logo ? 35 : 14, 19);
  doc.setFont(font, 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...C.muted);
  doc.text('Levantamiento de Presupuesto', logo ? 35 : 14, 25);

  doc.setFont(font, 'bold');
  doc.setFontSize(22);
  doc.setTextColor(...C.orange);
  doc.text('PRESUPUESTO', w - 14, 19, { align: 'right' });
  doc.setFont(font, 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...C.text);
  doc.text(`Folio: ${folio}`, w - 14, 25.5, { align: 'right' });
  doc.text(`Fecha: ${fecha}`, w - 14, 30.5, { align: 'right' });

  doc.setDrawColor(...C.row);
  doc.setLineWidth(0.4);
  doc.line(14, 35, w - 14, 35);

  // ── Datos del proyecto ──
  let y = sectionTitle(doc, font, 'DATOS DEL PROYECTO', 43);
  const datos = [
    ['PROYECTO:', projectName || ''],
    ['CONTRATO:', numeroContrato || 'Sin número'],
    ['UBICACIÓN:', ubicacion || 'Sin especificar'],
    ['ELABORÓ:', generatedBy || 'GenerIA'],
  ];
  datos.forEach((row, i) => {
    doc.setFillColor(...(i % 2 === 0 ? C.row : C.bg));
    doc.rect(14, y, w - 28, 7, 'F');
    doc.setFont(font, 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...C.cyan);
    doc.text(row[0], 17, y + 4.8);
    doc.setFont(font, 'normal');
    doc.setTextColor(...C.white);
    doc.text(String(row[1]), 50, y + 4.8);
    y += 7;
  });

  // ── Conceptos (solo precios de cliente: PU Solo M.O. y PU Con Material) ──
  const priced = rows.map((r) => {
    const { puMo, puMat } = clientPrices(r);
    return {
      ...r,
      puMo,
      puMat,
      importeMo: puMo === null ? null : r.cantidad * puMo,
      importeMat: puMat === null ? null : r.cantidad * puMat,
    };
  });
  const subtotalMo = priced.reduce((acc, r) => acc + (r.importeMo || 0), 0);
  const subtotalMat = priced.reduce((acc, r) => acc + (r.importeMat || 0), 0);

  // Columnas dinámicas según el modo elegido
  const head = ['CLAVE', 'DESCRIPCIÓN', 'CANTIDAD', 'UNIDAD'];
  if (showMo) head.push('PU SOLO M.O.', 'IMPORTE SOLO M.O.');
  if (showMat) head.push('PU CON MATERIAL', 'IMPORTE CON MATERIAL');

  const body = priced.map((r) => {
    const cols = [r.clave, r.descripcion || '', fmtQty(r.cantidad), r.unidad || ''];
    if (showMo) cols.push(r.puMo === null ? '—' : fmtMXN(r.puMo), r.importeMo === null ? '—' : fmtMXN(r.importeMo));
    if (showMat) cols.push(r.puMat === null ? '—' : fmtMXN(r.puMat), r.importeMat === null ? '—' : fmtMXN(r.importeMat));
    return cols;
  });

  // Ancho de la descripción se ajusta para llenar el espacio disponible (A4 landscape ≈ 269 mm)
  const priceGroups = (showMo ? 1 : 0) + (showMat ? 1 : 0);
  const claveW = 22, cantW = 22, uniW = 16, puW = 30, impW = 36;
  const fixedW = claveW + cantW + uniW + priceGroups * (puW + impW);
  const descW = 269 - fixedW;

  const columnStyles = {
    0: { cellWidth: claveW, fontStyle: 'bold', textColor: C.cyan },
    1: { cellWidth: descW },
    2: { cellWidth: cantW, halign: 'right' },
    3: { cellWidth: uniW, halign: 'center' },
  };
  let idx = 4;
  if (showMo) {
    columnStyles[idx++] = { cellWidth: puW, halign: 'right' };
    columnStyles[idx++] = { cellWidth: impW, halign: 'right', fontStyle: 'bold', textColor: C.white };
  }
  if (showMat) {
    columnStyles[idx++] = { cellWidth: puW, halign: 'right' };
    columnStyles[idx++] = { cellWidth: impW, halign: 'right', fontStyle: 'bold', textColor: C.white };
  }

  y = sectionTitle(doc, font, 'CONCEPTOS', y + 9);
  autoTable(doc, {
    startY: y,
    head: [head],
    body,
    theme: 'plain',
    styles: { font, fontStyle: 'normal', fontSize: 7.5, textColor: C.text, fillColor: C.bg, cellPadding: 2.4, valign: 'middle', lineWidth: 0 },
    headStyles: { fillColor: C.blue, textColor: C.white, fontStyle: 'bold', fontSize: 7, halign: 'center' },
    alternateRowStyles: { fillColor: C.row },
    columnStyles,
    margin: { left: 14, right: 14, top: 18, bottom: 18 },
    willDrawPage: (data) => { if (data.pageNumber > 1) paintBackground(doc); },
    didDrawPage: (data) => drawFooter(doc, font, data.pageNumber),
  });

  // ── Totales ──
  const h = doc.internal.pageSize.getHeight();
  let ty = doc.lastAutoTable.finalY + 8;
  if (ty + 70 > h - 14) {
    doc.addPage();
    paintBackground(doc);
    drawFooter(doc, font, doc.internal.getNumberOfPages());
    ty = 20;
  }
  const boxX = w - 14 - 110;
  const boxW = 110;
  const totalRow = (label, value, fill, bold, size, color, height) => {
    doc.setFillColor(...fill);
    doc.rect(boxX, ty, boxW, height, 'F');
    doc.setFont(font, bold ? 'bold' : 'normal');
    doc.setFontSize(size);
    doc.setTextColor(...color);
    doc.text(label, boxX + 4, ty + height / 2 + size * 0.12);
    doc.text(value, boxX + boxW - 4, ty + height / 2 + size * 0.12, { align: 'right' });
    ty += height;
  };
  const totalBlock = (titulo, subtotal) => {
    doc.setFillColor(...C.blue);
    doc.rect(boxX, ty, boxW, 7, 'F');
    doc.setFont(font, 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...C.white);
    doc.text(titulo, boxX + 4, ty + 4.8);
    ty += 7;
    totalRow('SUBTOTAL', fmtMXN(subtotal), C.row, false, 9, C.text, 8);
    totalRow(`IVA ${Math.round(IVA_RATE * 100)}%`, fmtMXN(subtotal * IVA_RATE), C.bg, false, 9, C.text, 8);
    totalRow('TOTAL', fmtMXN(subtotal * (1 + IVA_RATE)), C.orange, true, 13, C.white, 12);
    ty += 4;
  };

  if (showMo && subtotalMo > 0) totalBlock(showMat ? 'OPCIÓN A · SOLO MANO DE OBRA' : 'SOLO MANO DE OBRA', subtotalMo);
  if (showMat && (subtotalMat > 0 || !showMo || subtotalMo === 0)) {
    const titulo = showMo && subtotalMo > 0 ? 'OPCIÓN B · CON MATERIAL' : 'CON MATERIAL';
    totalBlock(titulo, subtotalMat);
  }

  const safeName = (projectName || 'contrato').trim().replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ _-]/g, '').trim().replace(/\s+/g, '_');
  const filename = `Presupuesto_${safeName}_${new Date().toISOString().slice(0, 10)}.pdf`;
  return { blob: doc.output('blob'), filename, folio };
};
